import React, { useState, useEffect } from 'react';
import { IntegrationRequest } from '../../lib/tenant-api';
import Modal from '../Modal';
import type { IntegrationConfigProps } from './types';

export const ShopifyConfig: React.FC<IntegrationConfigProps> = ({
  isOpen,
  onClose,
  integration,
  createIntegrationMutation,
  updateIntegrationMutation
}) => {

  const [formData, setFormData] = useState({
    name: '',
    shop_name: '',
    api_key: '',
    api_secret: '',
    access_token: '',
    platform_type: 'private' as 'private' | 'partner',
    api_version: '2024-07',
    is_enabled: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditing = !!integration;

  useEffect(() => {
    if (integration) {
      setFormData({
        name: integration.name,
        shop_name: integration.config.shop_name || '',
        api_key: integration.config.api_key || '',
        api_secret: integration.config.api_secret || '',
        access_token: integration.config.access_token || '',
        platform_type: integration.config.platform_type || 'private',
        api_version: integration.config.api_version || '2024-07',
        is_enabled: integration.is_enabled,
      });
    } else {
      setFormData({
        name: '',
        shop_name: '',
        api_key: '',
        api_secret: '',
        access_token: '',
        platform_type: 'private',
        api_version: '2024-07',
        is_enabled: true,
      });
    }
    setErrors({});
  }, [integration, isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Integration name is required';
    }

    if (!formData.shop_name.trim()) {
      newErrors.shop_name = 'Shop name is required';
    }

    if (formData.platform_type === 'partner') {
      if (!formData.api_key.trim()) {
        newErrors.api_key = 'API key is required for partner Shopify';
      }
      if (!formData.api_secret.trim()) {
        newErrors.api_secret = 'API secret is required for partner Shopify';
      }
    } else {
      if (!formData.access_token.trim()) {
        newErrors.access_token = 'Access token is required for private Shopify';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const integrationData: IntegrationRequest = {
      integration_type: 'shopify',
      name: formData.name,
      config: {
        shop_name: formData.shop_name,
        api_key: formData.api_key,
        api_secret: formData.api_secret,
        access_token: formData.access_token,
        platform_type: formData.platform_type,
        api_version: formData.api_version
      },
      is_enabled: formData.is_enabled,
    };

    if (isEditing && integration) {
      updateIntegrationMutation.mutate({
        id: integration.id,
        data: integrationData,
      });
    } else {
      createIntegrationMutation.mutate(integrationData);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };

      return newData;
    });

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  const isLoading = createIntegrationMutation.isLoading || updateIntegrationMutation.isLoading;
  return <Modal
    isOpen={isOpen}
    onClose={handleClose}
    title={isEditing ? `Edit Shopify Integration` : `Connect Shopify`}
  >
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Integration Name */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
          Integration Name
        </label>
        <input
          type="text"
          id="name"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          className={`w-full px-3 py-2 border  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.name ? 'border-red-300' : 'border-gray-300'
            }`}
          placeholder="e.g., My Shopify Integration"
          disabled={isLoading}
        />
        {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Shopify Platform Type
        </label>
        <div className="space-y-3">
          <div className="flex items-center">
            <input
              type="radio"
              id="private"
              name="platform_type"
              value="private"
              checked={formData.platform_type === 'private'}
              onChange={(e) => handleInputChange('platform_type', e.target.value)}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
              disabled={isLoading}
            />
            <label htmlFor="self_hosted" className="ml-3 block text-sm text-gray-900">
              <span className="font-medium">Private App</span>
              <span className="block text-gray-500 text-xs mt-1">
                For private apps managed by you
              </span>
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="radio"
              id="partner"
              name="platform_type"
              value="partner"
              checked={formData.platform_type === 'partner'}
              onChange={(e) => handleInputChange('platform_type', e.target.value)}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
              disabled={isLoading}
            />
            <label htmlFor="wordpress_com" className="ml-3 block text-sm text-gray-900">
              <span className="font-medium">Partner App</span>
              <span className="block text-gray-500 text-xs mt-1">
                For apps hosted by partner developers
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Site URL */}
      <div>
        <label htmlFor="shop_name" className="block text-sm font-medium text-gray-700 mb-2">
          Shop Name
        </label>
        <input
          type="text"
          id="shop_name"
          value={formData.shop_name}
          onChange={(e) => handleInputChange('shop_name', e.target.value)}
          className={`w-full px-3 py-2 border  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.shop_name ? 'border-red-300' : 'border-gray-300'
            }`}
          placeholder="e.g., myshopify"
          disabled={isLoading}
        />
        {errors.shop_name && <p className="mt-1 text-sm text-red-600">{errors.shop_name}</p>}
      </div>

      {formData.platform_type === 'private' && (
        <div>
          <label htmlFor="access_token" className="block text-sm font-medium text-gray-700 mb-2">
            Access Token
          </label>
          <input
            type="text"
            id="access_token"
            value={formData.access_token}
            onChange={(e) => handleInputChange('access_token', e.target.value)}
            className={`w-full px-3 py-2 border  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.access_token ? 'border-red-300' : 'border-gray-300'
              }`}
            disabled={isLoading}
          />
          {errors.access_token && <p className="mt-1 text-sm text-red-600">{errors.access_token}</p>}
        </div>

      )}

      {formData.platform_type === 'partner' && (
        <>
          <div>
            <label htmlFor="api_key" className="block text-sm font-medium text-gray-700 mb-2">
              API Key
            </label>
            <input
              type="text"
              id="api_key"
              value={formData.api_key}
              onChange={(e) => handleInputChange('api_key', e.target.value)}
              className={`w-full px-3 py-2 border  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.api_key ? 'border-red-300' : 'border-gray-300'
                }`}
              disabled={isLoading}
            />
            {errors.api_key && <p className="mt-1 text-sm text-red-600">{errors.api_key}</p>}
          </div>
          <div>
            <label htmlFor="api_secret" className="block text-sm font-medium text-gray-700 mb-2">
              API Secret
            </label>
            <input
              type="text"
              id="api_secret"
              value={formData.api_secret}
              onChange={(e) => handleInputChange('api_secret', e.target.value)}
              className={`w-full px-3 py-2 border  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.api_secret ? 'border-red-300' : 'border-gray-300'
                }`}
              disabled={isLoading}
            />
            {errors.api_secret && <p className="mt-1 text-sm text-red-600">{errors.api_secret}</p>}
          </div>
        </>
      )}
      <div>
        <label htmlFor="api_version" className="block text-sm font-medium text-gray-700 mb-2">
          API Version
        </label>
        <input
          type="text"
          id="api_version"
          value={formData.api_version}
          onChange={(e) => handleInputChange('api_version', e.target.value)}
          className={`w-full px-3 py-2 border  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.api_version ? 'border-red-300' : 'border-gray-300'
            }`}
          disabled={isLoading}
        />
        {errors.api_version && <p className="mt-1 text-sm text-red-600">{errors.api_version}</p>}
      </div>
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={handleClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300  hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent  hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white inline" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {isEditing ? 'Updating...' : 'Connecting...'}
            </>
          ) : (
            isEditing ? 'Update Integration' : 'Connect Shopify'
          )}
        </button>
      </div>
    </form>
  </Modal>

}
